import React, { useState, useEffect } from 'react';
import { Form, Button, ListGroup } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useCreatePaymentMutation, useUploadPaymentImagesMutation } from '../../slices/paymentsApiSlice';
import { useGetLandlordsQuery } from '../../slices/landlordsApiSlice';
import { useSelector } from 'react-redux';

const AddPaymentScreen = () => {
  const navigate = useNavigate();
  const { userInfo } = useSelector((state) => state.auth);

  // Form State
  const [formData, setFormData] = useState({
    date: '',
    account: '',
    accountName: '',
    subAccount: '',
    details: '',
    amount: '',
    category: '',
    notes: '',
    images: [],
  });

  // Landlord Search State
  const [landlordSearch, setLandlordSearch] = useState('');
  const [filteredLandlords, setFilteredLandlords] = useState([]);
  const [selectedLandlord, setSelectedLandlord] = useState(null);

  // Queries & Mutations
  const { data: landlordsData } = useGetLandlordsQuery({ keyword: landlordSearch });
  const [createPayment, { isLoading, error }] = useCreatePaymentMutation();
  const [uploadPaymentImages] = useUploadPaymentImagesMutation();

  // Fetch and filter landlords dynamically
  useEffect(() => {
    if (landlordSearch.length >= 3 && landlordsData?.landlords) {
      setFilteredLandlords(
        landlordsData.landlords.filter((landlord) =>
          landlord.name.toLowerCase().includes(landlordSearch.toLowerCase())
        )
      );
    } else {
      setFilteredLandlords([]);
    }
  }, [landlordSearch, landlordsData]);

  const handleLandlordSelect = (landlord) => {
    setSelectedLandlord(landlord);
    setFormData((prevData) => ({
      ...prevData,
      accountName: landlord.name,
    }));
    setFilteredLandlords([]);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));

    if (name === 'accountName') {
      setLandlordSearch(value);
    }
  };

  const submitHandler = async (e) => {
    e.preventDefault();
    try {
      await createPayment({
        ...formData,
        accountName: selectedLandlord?.name || formData.accountName,
        username: userInfo.name || '',
      }).unwrap();
      toast.success('Payment added successfully');
      navigate('/admin/paymentlist');
    } catch (err) {
      toast.error(err?.data?.message || 'Failed to add payment');
    }
  };

  const validateFiles = (files) => {
    const allowedTypes = ['image/jpeg', 'image/png', 'image/jpg'];
    const maxSize = 5 * 1024 * 1024; // 5MB
    for (let file of files) {
      if (!allowedTypes.includes(file.type)) {
        toast.error(`File type not allowed: ${file.type}`);
        return false;
      }
      if (file.size > maxSize) {
        toast.error(`File size exceeds 5MB: ${file.name}`);
        return false;
      }
    }
    return true;
  };

  const uploadImageHandler = async (files) => {
    if (!validateFiles(files)) return;

    const formDataUpload = new FormData();
    Array.from(files).forEach((file) => formDataUpload.append('images', file));

    try {
      const res = await uploadPaymentImages(formDataUpload).unwrap();
      toast.success(res.message);
      setFormData((prevData) => ({
        ...prevData,
        images: [...prevData.images, ...res.images],
      }));
    } catch (err) {
      toast.error(err?.data?.message || err.error);
    }
  };

  const removeImageHandler = (index) => {
    setFormData((prevData) => ({
      ...prevData,
      images: prevData.images.filter((_, i) => i !== index),
    }));
  };

  const [calcDisplay, setCalcDisplay] = useState('');
  const handleCalculatorClick = (value) => {
  if (value === '=') {
    try {
      setCalcDisplay(eval(calcDisplay).toString());
    } catch {
      setCalcDisplay('Error');
    }
  } else if (value === 'C') {
    setCalcDisplay('');
  } else {
    setCalcDisplay((prev) => prev + value);
  }
};

  return (
    <div className="container mt-4">
      <h1>Add New Payment</h1>
      <Form onSubmit={submitHandler}>
        <Form.Group controlId="date" className="mb-3">
          <Form.Label>Date</Form.Label>
          <Form.Control type="date" name="date" value={formData.date} onChange={handleChange} required />
        </Form.Group>

        <Form.Group controlId="account" className="mb-3">
          <Form.Label>Account Type</Form.Label>
          <Form.Control as="select" name="account" value={formData.account} onChange={handleChange} required>
            <option value="Credit">Credit</option>
            <option value="Debit">Debit</option>
          </Form.Control>
        </Form.Group>

        <Form.Group controlId="accountName" className="mb-3">
          <Form.Label>Account Name</Form.Label>
          <Form.Control
            type="text"
            name="accountName"
            value={formData.accountName}
            placeholder="Search account name"
            onChange={handleChange}
            required
          />
          {landlordSearch.length >= 3 && filteredLandlords.length > 0 && (
            <ListGroup className="dropdown-menu mt-2">
              {filteredLandlords.map((landlord) => (
                <ListGroup.Item key={landlord._id} action onClick={() => handleLandlordSelect(landlord)}>
                  {landlord.name} ({landlord.company || 'No company'})
                </ListGroup.Item>
              ))}
            </ListGroup>
          )}
        </Form.Group>
        <Form.Group controlId="subAccount" className="mb-3">
                    <Form.Label>Sub-Account</Form.Label>
                    <Form.Control
                      as="select"
                      name="subAccount"
                      value={formData.subAccount}
                      onChange={handleChange}
                      required
                    >
                      <option value="LOAN">LOAN</option>
                      <option value="EXPENSE">EXPENSE</option>
                      <option value="SERVICES">SERVICES</option>
                      <option value="OTHER INCOME">OTHER INCOME</option>
                      <option value="PAYMENT">PAYMENT</option>
                      <option value="DISCOUNT">DISCOUNT</option>
                    </Form.Control>
                  </Form.Group>
          
                  <Form.Group controlId="details" className="mb-3">
                    <Form.Label>Details</Form.Label>
                    <Form.Control
                      type="text"
                      name="details"
                      value={formData.details}
                      placeholder="Enter details"
                      onChange={handleChange}
                      required
                    />
        </Form.Group>

        <Form.Group controlId="amount" className="mb-3">
          <Form.Label>Amount</Form.Label>
          <Form.Control type="number" name="amount" value={formData.amount} onChange={handleChange} required />
        </Form.Group>
        {/* Calculator UI */}
<div className="mb-4 p-3 border rounded" style={{ maxWidth: '230px' }}>
  <Form.Label>Mini Calculator</Form.Label>
  <Form.Control type="text" value={calcDisplay} readOnly className="mb-2" />
  {['7','8','9','/','4','5','6','*','1','2','3','-','0','.','=','+','C'].map((char) => (
    <Button
      key={char}
      variant="light"
      className="m-1"
      onClick={() => handleCalculatorClick(char)}
      style={{ width: '40px' }}
    >
      {char}
    </Button>
  ))}
  <div className="mt-2">
    <Button
      variant="outline-primary"
      onClick={() =>
        setFormData((prev) => ({ ...prev, amount: calcDisplay }))
      }
      disabled={calcDisplay === '' || calcDisplay === 'Error'}
    >
      Copy to Amount Field
    </Button>
  </div>
</div>
        
        <Form.Group controlId="category" className="mb-3">
                    <Form.Label>Category</Form.Label>
                    <Form.Control
                      as="select"
                      name="category"
                      value={formData.category}
                      onChange={handleChange}
                    >
                      
                      <option value="">Select</option>
                      <option value="RentPayment">RentPayment</option>
                      <option value="RentCommission">RentCommission</option>
                      <option value="Maintenance">Maintenance</option>
                      <option value="Deposit">Deposit</option>
                      <option value="Other">Other</option>
                                             
                    </Form.Control>
                  </Form.Group>
          
                  <Form.Group controlId="notes" className="mb-3">
                    <Form.Label>Notes</Form.Label>
                    <Form.Control
                      as="textarea"
                      name="notes"
                      value={formData.notes}
                      placeholder="Enter additional notes"
                      rows={3}
                      onChange={handleChange}
                    />
                  </Form.Group>
                  <Form.Group controlId="username" className="mb-3">
                    <Form.Label>User</Form.Label>
                    <Form.Control
                      as="textarea"
                      name="notes"
                      value={userInfo.name}
    
                      rows={3}
                      onChange={handleChange}
                    />
                  </Form.Group>
        <Form.Group controlId="images" className="mb-3">
          <Form.Label>Images</Form.Label>
          <Form.Control type="file" multiple onChange={(e) => uploadImageHandler(e.target.files)} />
          <div className="mt-3">
            {formData.images.map((img, idx) => (
              <div key={idx} style={{ display: 'inline-block', position: 'relative', marginRight: '10px' }}>
                <img
                  src={img}
                  alt={`Uploaded ${idx}`}
                  className="img-fluid"
                  style={{ maxWidth: '100px', height: '100px', objectFit: 'cover' }}
                />
                <Button
                  variant="danger"
                  size="sm"
                  onClick={() => removeImageHandler(idx)}
                  style={{
                    position: 'absolute',
                    top: '0',
                    right: '0',
                    borderRadius: '50%',
                    padding: '2px 5px',
                  }}
                >
                  ✖
                </Button>
              </div>
            ))}
          </div>
        </Form.Group>

        <Button type="submit" variant="primary" disabled={isLoading}>
          {isLoading ? 'Adding Payment...' : 'Add Payment'}
        </Button>

        {error && <p className="text-danger">{error?.data?.message || 'Error adding payment'}</p>}
      </Form>
    </div>
  );
};

export default AddPaymentScreen;